import {
    Button,
    Card,
    CardHeader,
    FluentProvider,
    makeStyles,
    tokens,
    typographyStyles,
} from '@fluentui/react-components';
import { Dismiss24Regular, WrenchScrewdriverRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useMcpPromotionDialogContext } from '../../../hooks/context/dialogs';
import { useCurrentFluent2Theme } from '../../../hooks/styling';

interface McpPromotionCardProps {
    onDismiss?: () => void;
}

const messages = defineMessages({
    closeButtonAriaLabel: {
        id: 'QuickActionCard_CloseButton_AriaLabel',
        defaultMessage: 'Close',
        description: 'Aria label for quick action card "Close" button',
    },
});

/**
 * Styles
 */

const useCardStyles = makeStyles({
    root: {
        width: '305px',
    },
    icon: {
        height: '32px',
        width: '32px',
    },
    titleHeading: {
        margin: 0,
    },
    // The title is rendered as a button so the card's primary action is keyboard operable
    // (focusable + Enter/Space) per MAS 2.1.1; these resets keep it looking like plain title text.
    titleButton: {
        ...typographyStyles.body1Strong,
        background: 'none',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        margin: 0,
        padding: 0,
        textAlign: 'left',
        ':focus-visible': {
            borderRadius: tokens.borderRadiusMedium,
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '2px',
        },
    },
    content: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
    },
});

/**
 * END Styles
 */

export const McpPromotionCard: React.FC<McpPromotionCardProps> = React.memo((props: McpPromotionCardProps) => {
    const { onDismiss } = props;

    // Intl hooks
    const { formatMessage } = useIntl();

    const theme = useCurrentFluent2Theme();
    const { openSurface } = useMcpPromotionDialogContext();

    // Style hooks
    const cardStyles = useCardStyles();

    // Callback hooks
    const onDismissHandler = React.useCallback(
        (
            event: React.MouseEvent<
                HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement,
                MouseEvent
            >
        ) => {
            if (onDismiss) {
                // Block event from propagating up to the card's click handler so that you don't also open the dialog when you attempt to dismiss the card
                event.stopPropagation();

                onDismiss();
            }
        },
        [onDismiss]
    );

    const onCardClickHandler = React.useCallback(() => {
        openSurface({});
    }, [openSurface]);

    // Keyboard-operable primary action. The whole card remains mouse-clickable via the Card's
    // onClick; stopPropagation prevents the action from firing twice when the title is clicked.
    const onTitleActivate = React.useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            openSurface({});
        },
        [openSurface]
    );

    return (
        <FluentProvider theme={theme}>
            <Card onClick={onCardClickHandler} focusMode="off" className={cardStyles.root}>
                <CardHeader
                    image={<WrenchScrewdriverRegular className={cardStyles.icon} />}
                    header={
                        <h3 className={cardStyles.titleHeading}>
                            <button type="button" className={cardStyles.titleButton} onClick={onTitleActivate}>
                                <FormattedMessage
                                    id="QuickActionCard_McpPromotionActionTitle_Text"
                                    defaultMessage="Try out Dev Box MCP Server!"
                                    description="Title of the 'Dev Box MCP Server' quick action card"
                                />
                            </button>
                        </h3>
                    }
                    action={
                        <Button
                            appearance="subtle"
                            aria-label={formatMessage(messages.closeButtonAriaLabel)}
                            icon={<Dismiss24Regular />}
                            onClick={onDismissHandler}
                        />
                    }
                />
                <div className={cardStyles.content}>
                    <FormattedMessage
                        id="QuickActionCard_McpPromotionActionSubtitle_Text"
                        defaultMessage="Add dev box tools to Github Copilot inside of Visual Studio Code."
                        description="Explanatory text for the 'MCP Promotion' quick action card"
                    />
                </div>
            </Card>
        </FluentProvider>
    );
});

export default McpPromotionCard;
